import React, { useEffect, useRef, useState } from 'react';
import { PageTitle } from '@gsa/afp-component-library';
import { useLazyQuery } from '@apollo/client';
import ContractSearchSection from './contract-search-section';
import ContractSearchTable from './contract-search-table';
import { SEARCH_OPTIONS } from './contract-search-helpers';
import { GET_CONTRACT_HEADERS_BY_CRITERIA } from './contract-search-gql';
import { ContractSearchBreadcrumbs } from './contract-search-breadcrumbs';
import { getContractYear } from '../components/contract-helpers';
import useHistoryState from '../../../hooks/use-history-state';

const ContractSearch = () => {
  // The last search run on this page, kept on the history entry so the
  // browser back button brings the results back (breadcrumbs start clean).
  const [lastSearch, setLastSearch] = useHistoryState('contractSearch', null);
  const restoredOffset = useRef(lastSearch?.offset);

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(lastSearch?.limit ?? 10);
  const [contracts, setContracts] = useState([]);

  const [searchKey, setSearchKey] = useState(
    lastSearch?.searchKey ?? SEARCH_OPTIONS[0].value,
  );
  const [searchValue, setSearchValue] = useState(
    lastSearch?.searchValue ?? '',
  );
  const [searchMessage, setSearchMessage] = useState('');

  const [getContractHeadersByCriteria, { loading, called }] = useLazyQuery(
    GET_CONTRACT_HEADERS_BY_CRITERIA,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        setOffset(restoredOffset.current ?? 0);
        restoredOffset.current = undefined;

        // consolidate contracts by contractUpiid and keep the one with the latest year
        const contractYearMap = (res.getContractHeadersByCriteria?.rows || [])
          .map((contract) => ({
            ...contract,
            // NOTE: displayContractYear is used as the accessor for the contract year column
            displayContractYear: getContractYear(contract),
          }))
          .reduce((contractMap, contract) => {
            const existing = contractMap.get(contract.contractUpiid);
            if (
              !existing ||
              existing.displayContractYear < contract.displayContractYear
            ) {
              contractMap.set(contract.contractUpiid, contract);
            }
            return contractMap;
          }, new Map());

        setContracts(Array.from(contractYearMap.values()));
        setSearchMessage(
          contractYearMap.size === 0
            ? `There are no contracts matching the search criteria.`
            : `Search results for ${searchKey} "${searchValue}"`,
        );
      },
    },
  );

  const runSearch = (key, value) =>
    getContractHeadersByCriteria({
      variables: {
        criteria: { [key]: value },
        // NOTE: Since we use different fields to display the contract year,
        // we need to fetch all contract headers to get the latest contract year
        limit: 10000,
      },
    });

  useEffect(() => {
    if (lastSearch) {
      runSearch(lastSearch.searchKey, lastSearch.searchValue);
    }
  }, []);

  const onSearch = () => {
    const trimmedSearchValue = String(searchValue).trim();
    if (!trimmedSearchValue) {
      return;
    }

    setLastSearch({ searchKey, searchValue: trimmedSearchValue, offset: 0, limit });
    runSearch(searchKey, trimmedSearchValue);
  };

  const onPageChange = (currentPage, itemsPerPage) => {
    let newOffset;
    if (itemsPerPage === limit) {
      newOffset = (currentPage - 1) * itemsPerPage;
    } else {
      newOffset = 0; // reset to page 1
      setLimit(itemsPerPage);
    }
    setOffset(newOffset);
    if (lastSearch) {
      setLastSearch({ ...lastSearch, offset: newOffset, limit: itemsPerPage });
    }
  };

  const onSort = (order) => {
    // Only support sort by displayContractYear
    if (order[0]?.[0] === 'displayContractYear') {
      setContracts((prev) =>
        prev.toSorted((a, b) => {
          // Treat non-numeric values (like emDash) as 0 for sorting
          const yearA =
            typeof a.displayContractYear === 'number'
              ? a.displayContractYear
              : 0;
          const yearB =
            typeof b.displayContractYear === 'number'
              ? b.displayContractYear
              : 0;
          return order[0][1].toUpperCase() === 'ASC'
            ? yearA - yearB
            : yearB - yearA;
        }),
      );
    }
  };

  return (
    <div className="margin-bottom-8">
      <ContractSearchBreadcrumbs />
      <div className="grid-row margin-bottom-5 padding-top-1 padding-bottom-1 gap-1">
        <div className="grid-col-10">
          <PageTitle
            title="Contracts"
            aria-label="Contracts"
            className="margin-bottom-0"
          />
          <div>
            Search contracts by contract number, contract uPIID, solicitation
            uPIID, solicitation name and Vendor name/UEI.
          </div>
        </div>
      </div>
      <ContractSearchSection
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onSearch={onSearch}
        initialVendorName={
          lastSearch?.searchKey === 'vendorNameOrUei'
            ? lastSearch.searchValue
            : ''
        }
      />
      {called && !loading && (
        <div className="text-bold margin-top-05">{searchMessage}</div>
      )}
      <ContractSearchTable
        loading={loading}
        data={contracts.slice(offset, offset + limit)}
        count={contracts.length}
        offset={offset}
        limit={limit}
        onPageChange={onPageChange}
        onSort={onSort}
      />
    </div>
  );
};

export default ContractSearch;
